import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	TemplateRef,
	ViewChild,
	ViewContainerRef
} from '@angular/core';
import { DateRange, MatCalendar } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import * as moment from 'moment/moment';
import { Subscription } from 'rxjs';
import { Utils } from 'app/utils';

export type DashboardDatePreset = 'today' | 'yesterday' | 'last7' | 'last30' | 'lastMonth' | 'custom';

export interface DashboardDateSelection {
	preset: DashboardDatePreset;
	startDate: string;
	endDate: string;
}

const DASHBOARD_DATE_FORMATS: MatDateFormats = {
	parse: {
		dateInput: null
	},
	display: {
		dateInput: { year: 'numeric', month: 'numeric', day: 'numeric' },
		monthYearLabel: { year: 'numeric', month: 'long' },
		dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
		monthYearA11yLabel: { year: 'numeric', month: 'long' }
	}
};

@Component({
	selector: 'operator-dashboard-date-picker',
	templateUrl: './operator-dashboard-date-picker.component.html',
	styleUrls: ['./operator-dashboard-date-picker.component.scss'],
	providers: [{ provide: MAT_DATE_FORMATS, useValue: DASHBOARD_DATE_FORMATS }]
})
export class OperatorDashboardDatePickerComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() preset: DashboardDatePreset = 'today';
	@Input() startDate = '';
	@Input() endDate = '';
	/** Presets follow the Philippines calendar. Reports APIs send a Manila timezone offset. */
	@Input() usePhilippinesCalendar = false;
	@Input() disableFutureDates = false;
	@Input() showCustomPreset = true;

	@Output() apply = new EventEmitter<DashboardDateSelection>();
	@Output() dismiss = new EventEmitter<void>();

	@ViewChild('trigger') trigger?: ElementRef<HTMLButtonElement>;
	@ViewChild('panelTemplate') panelTemplate?: TemplateRef<unknown>;
	@ViewChild('pickerOutlet', { read: ViewContainerRef }) pickerOutlet?: ViewContainerRef;
	@ViewChild('leftCalendar') leftCalendar?: MatCalendar<Date>;
	@ViewChild('rightCalendar') rightCalendar?: MatCalendar<Date>;

	private readonly allPresets: Array<{ label: string; value: DashboardDatePreset }> = [
		{ label: 'Today', value: 'today' },
		{ label: 'Yesterday', value: 'yesterday' },
		{ label: 'Last 7 days', value: 'last7' },
		{ label: 'Last 30 days', value: 'last30' },
		{ label: 'Last Month', value: 'lastMonth' },
		{ label: 'Custom', value: 'custom' }
	];

	get presets(): Array<{ label: string; value: DashboardDatePreset }> {
		return this.showCustomPreset
			? this.allPresets
			: this.allPresets.filter(item => item.value !== 'custom');
	}

	open = false;
	draftPreset: DashboardDatePreset = 'today';
	selectedRange: DateRange<Date> = new DateRange<Date>(null, null);
	leftMonth = this.startOfMonth(new Date());
	rightMonth = this.addMonths(this.startOfMonth(new Date()), 1);

	private awaitingRangeEnd = false;
	private syncingMonths = false;
	private monthSync?: Subscription;
	private overlayRef?: OverlayRef;
	private overlayEvents?: Subscription;

	constructor(private overlay: Overlay) {}

	get maxSelectableDate(): Date | null {
		return this.disableFutureDates ? this.calendarToday() : null;
	}

	readonly selectableDateFilter = (date: Date | null): boolean => {
		if (!date) {
			return false;
		}
		if (!this.disableFutureDates) {
			return true;
		}
		return this.startOfDay(date).getTime() <= this.calendarToday().getTime();
	};

	get dateFilterLabel(): string {
		const start = moment(this.startDate, 'YYYY-MM-DD', true);
		const end = moment(this.endDate, 'YYYY-MM-DD', true);
		if (!start.isValid() || !end.isValid()) {
			return moment(this.calendarToday()).format('MMMM D, YYYY') + ' (today)';
		}
		switch (this.preset) {
			case 'today':
				return `${start.format('MMMM D, YYYY')} (today)`;
			case 'yesterday':
				return `${start.format('MMMM D, YYYY')} (yesterday)`;
			case 'last7':
				return `${start.format('MMMM D, YYYY')} (last 7 days)`;
			case 'last30':
				return `${start.format('MMMM D, YYYY')} (last 30 days)`;
			case 'lastMonth':
				return `${this.formatSameMonthRange(start, end)} (Last Month)`;
			case 'custom':
				return `${this.formatDateRange(start, end)} (Custom)`;
			default:
				return start.format('MMMM D, YYYY');
		}
	}

	toggle(): void {
		if (this.overlayRef?.hasAttached()) {
			this.close();
			return;
		}
		this.openPanel();
	}

	close(): void {
		this.detachPanel();
		this.dismiss.emit();
	}

	ngOnInit(): void {
		this.syncDraftFromInputs();
	}

	ngAfterViewInit(): void {
		this.showMonths(this.leftMonth);
	}

	ngOnDestroy(): void {
		this.monthSync?.unsubscribe();
		this.overlayEvents?.unsubscribe();
		this.overlayRef?.dispose();
	}

	selectPreset(preset: DashboardDatePreset, revealRange = true): void {
		this.draftPreset = preset;
		this.awaitingRangeEnd = false;
		if (preset === 'custom') {
			return;
		}
		const [start, end] = this.rangeForPreset(preset);
		this.selectedRange = new DateRange(start, end);
		if (revealRange) {
			this.showMonths(this.startOfMonth(start));
		}
	}

	onDateSelected(date: Date | null): void {
		if (!date) {
			return;
		}
		const selected = this.startOfDay(date);
		if (this.disableFutureDates && selected.getTime() > this.calendarToday().getTime()) {
			return;
		}
		this.draftPreset = 'custom';
		const start = this.selectedRange.start ? this.startOfDay(this.selectedRange.start) : null;
		if (!this.awaitingRangeEnd || !start) {
			this.selectedRange = new DateRange(selected, selected);
			this.awaitingRangeEnd = true;
			return;
		}
		this.selectedRange = selected < start
			? new DateRange(selected, start)
			: new DateRange(start, selected);
		this.awaitingRangeEnd = false;
	}

	isSelectedPreset(preset: DashboardDatePreset): boolean {
		return this.draftPreset === preset;
	}

	clear(): void {
		this.selectPreset('today');
	}

	generate(): void {
		const start = this.selectedRange.start;
		const end = this.selectedRange.end ?? start;
		if (!start || !end) {
			return;
		}
		let from = this.startOfDay(start);
		let to = this.startOfDay(end);
		if (this.disableFutureDates) {
			const today = this.calendarToday();
			if (from.getTime() > today.getTime()) {
				from = today;
			}
			if (to.getTime() > today.getTime()) {
				to = today;
			}
			if (from > to) {
				to = from;
			}
		} else if (from > to) {
			[from, to] = [to, from];
		}
		this.apply.emit({
			preset: this.draftPreset,
			startDate: this.formatCalendarDay(from),
			endDate: this.formatCalendarDay(to)
		});
		this.detachPanel();
	}

	private openPanel(): void {
		if (!this.trigger || !this.panelTemplate || !this.pickerOutlet) {
			return;
		}
		this.syncDraftFromInputs();
		if (!this.overlayRef) {
			const positionStrategy = this.overlay.position()
				.flexibleConnectedTo(this.trigger)
				.withFlexibleDimensions(false)
				.withPush(true)
				.withViewportMargin(16)
				.withPositions([
					{ originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
					{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
					{ originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
					{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 }
				]);
			this.overlayRef = this.overlay.create({
				positionStrategy,
				scrollStrategy: this.overlay.scrollStrategies.reposition(),
				hasBackdrop: true,
				backdropClass: 'operator-date-picker-backdrop',
				panelClass: 'operator-date-picker-overlay'
			});
			this.overlayEvents = new Subscription();
			this.overlayEvents.add(this.overlayRef.backdropClick().subscribe(() => this.close()));
			this.overlayEvents.add(this.overlayRef.keydownEvents().subscribe(event => {
				if (event.key === 'Escape') {
					this.close();
				}
			}));
		}
		this.overlayRef.attach(new TemplatePortal(this.panelTemplate, this.pickerOutlet));
		this.open = true;
		setTimeout(() => this.bindCalendars());
	}

	private detachPanel(): void {
		this.monthSync?.unsubscribe();
		this.monthSync = undefined;
		if (this.overlayRef?.hasAttached()) {
			this.overlayRef.detach();
		}
		this.open = false;
	}

	private bindCalendars(): void {
		this.showMonths(this.leftMonth);
		this.monthSync?.unsubscribe();
		this.monthSync = new Subscription();
		if (this.leftCalendar) {
			this.monthSync.add(this.leftCalendar.stateChanges.subscribe(() => this.onLeftMonthChanged()));
		}
		if (this.rightCalendar) {
			this.monthSync.add(this.rightCalendar.stateChanges.subscribe(() => this.onRightMonthChanged()));
		}
	}

	private onLeftMonthChanged(): void {
		if (this.syncingMonths || !this.leftCalendar) {
			return;
		}
		const next = this.startOfMonth(this.leftCalendar.activeDate);
		if (next.getTime() === this.leftMonth.getTime()) {
			return;
		}
		this.showMonths(next);
	}

	private onRightMonthChanged(): void {
		if (this.syncingMonths || !this.rightCalendar) {
			return;
		}
		const left = this.addMonths(this.startOfMonth(this.rightCalendar.activeDate), -1);
		if (left.getTime() === this.leftMonth.getTime()) {
			return;
		}
		this.showMonths(left);
	}

	private showMonths(left: Date): void {
		this.leftMonth = this.startOfMonth(left);
		this.rightMonth = this.addMonths(this.leftMonth, 1);
		if (!this.leftCalendar || !this.rightCalendar) {
			return;
		}
		this.syncingMonths = true;
		this.leftCalendar.activeDate = this.leftMonth;
		this.rightCalendar.activeDate = this.rightMonth;
		this.syncingMonths = false;
	}

	private syncDraftFromInputs(): void {
		this.awaitingRangeEnd = false;
		this.draftPreset = this.preset;
		const start = moment(this.startDate, 'YYYY-MM-DD', true);
		const end = moment(this.endDate, 'YYYY-MM-DD', true);
		if (start.isValid() && end.isValid()) {
			const startDate = this.toLocalDate(start);
			this.selectedRange = new DateRange(startDate, this.toLocalDate(end));
			this.leftMonth = this.startOfMonth(startDate);
			this.rightMonth = this.addMonths(this.leftMonth, 1);
			return;
		}
		this.selectPreset(this.preset === 'custom' ? 'today' : this.preset, false);
		const today = this.startOfMonth(this.calendarToday());
		this.leftMonth = today;
		this.rightMonth = this.addMonths(today, 1);
	}

	private rangeForPreset(preset: DashboardDatePreset): [Date, Date] {
		const today = this.calendarToday();
		if (preset === 'yesterday') {
			const yesterday = this.addDays(today, -1);
			return [yesterday, yesterday];
		}
		if (preset === 'last7') {
			return [this.addDays(today, -6), today];
		}
		if (preset === 'last30') {
			return [this.addDays(today, -29), today];
		}
		if (preset === 'lastMonth') {
			const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
			const end = new Date(today.getFullYear(), today.getMonth(), 0);
			return [start, end];
		}
		return [today, today];
	}

	private formatSameMonthRange(start: moment.Moment, end: moment.Moment): string {
		if (start.isSame(end, 'day')) {
			return start.format('MMMM D, YYYY');
		}
		if (start.isSame(end, 'month')) {
			return `${start.format('MMMM D')} – ${end.format('D, YYYY')}`;
		}
		return this.formatDateRange(start, end);
	}

	private formatDateRange(start: moment.Moment, end: moment.Moment): string {
		if (start.isSame(end, 'day')) {
			return start.format('MMMM D, YYYY');
		}
		if (start.isSame(end, 'year')) {
			return `${start.format('MMMM D')} – ${end.format('MMMM D, YYYY')}`;
		}
		return `${start.format('MMMM D, YYYY')} – ${end.format('MMMM D, YYYY')}`;
	}

	private calendarToday(): Date {
		return this.usePhilippinesCalendar
			? Utils.getPHTodayCalendarDate()
			: this.startOfDay(new Date());
	}

	private formatCalendarDay(date: Date): string {
		return this.usePhilippinesCalendar
			? Utils.formatPHCalendarDate(date)
			: moment(date).format('YYYY-MM-DD');
	}

	private toLocalDate(value: moment.Moment): Date {
		return new Date(value.year(), value.month(), value.date());
	}

	private startOfDay(date: Date): Date {
		return new Date(date.getFullYear(), date.getMonth(), date.getDate());
	}

	private startOfMonth(date: Date): Date {
		return new Date(date.getFullYear(), date.getMonth(), 1);
	}

	private addDays(date: Date, days: number): Date {
		return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
	}

	private addMonths(date: Date, months: number): Date {
		return new Date(date.getFullYear(), date.getMonth() + months, 1);
	}
}
